import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RecipeModal from '../../../../components/modal/RecipeModal';
import { SearchDropdownSelect } from '../../../../components/dropdown/SearchDropdownSelect';
import { DynamicQuantity } from './DynamicQuantity';
import { SaveButton } from '../../../../components/buttons/SaveButton';
import { CancelButton } from '../../../../components/buttons/CancelButton';
import { generateUUID } from '../../../../utils/UUIDGenerator';
import { RecipeOutputTable } from './RecipeOutputTable';
import { RecipeOutputModal } from './RecipeOutputModal';

export const RecipeFormComponent = ({ recipe, productsList, addProduct, onClose, handleSubmit, recipeLoaded }) => {
    const { t } = useTranslation('recipe');
    const [selectedProduct, setSelectedProduct] = useState({ id: -1, name: t('common:SELECT') });
    const [recipeOutput, setRecipeOutput] = useState([]);
    const [formValid, setFormValid] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    let recipeTitle = recipe.title;
    let recipeText = '';
    let dynamicQuantities = [];

    const handleAddProduct = (product, values) => {
        addProduct(product, values);
        setSelectedProduct({ id: -1, name: t('common:SELECT') });
    };

    useEffect(() => {
        if (recipeLoaded) {
            recipeText = recipe.recipeText;
            let container = document.getElementById('editable-content');
            let node = document.createTextNode(recipeText);
            container.appendChild(node);
            if (recipeText.length > 0 && recipe.dynamicQuantities != null && recipe.dynamicQuantities.length > 0) {
                setupDynamicRecipe();
            }
            if (recipe.recipeOutput != null) {
                setRecipeOutput(recipe.recipeOutput)
            }
        }

    }, [recipe]);

    const setupDynamicRecipe = () => {
        let content = [];
        let textStartIdx = 0;
        recipe.dynamicQuantities
            .sort((a, b) => a.idx > b.idx ? 1 : -1)
            .forEach((item, loopIdx) => {
                if (item.idx !== 0) {
                    let text = recipeText.substring(textStartIdx, item.idx);
                    let node = document.createTextNode(text);
                    content.push(node);
                }
                content.push(getButton(item.quantity));
                textStartIdx = item.idx;
                if (loopIdx + 1 === recipe.dynamicQuantities.length && recipeText.length > item.idx) {
                    let text = recipeText.substring(item.idx, recipeText.length);
                    let node = document.createTextNode(text);
                    content.push(node);
                }
            });
        let container = document.getElementById('editable-content');
        if (container.firstChild != null) {
            container.removeChild(container.firstChild);
        }
        content.forEach(child => {
            container.appendChild(child);
        });
    };

    const addDynamicQuantity = (value) => {
        let button = getButton(value.quantity);
        if (value.cursorIndex != null) {
            let container = document.getElementById('editable-content');
            let textNode = findChildNode(container, value.text);
            if (textNode) {
                let range = document.createRange();
                range.setStart(textNode, value.cursorIndex);
                range.setEnd(textNode, value.cursorIndex);

                let selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                range.deleteContents();
                range.insertNode(button);
                range.setStartAfter(button);
                range.setEndAfter(button);
            } else {
                addButtonAtEnd(button);
            }
        } else {
            addButtonAtEnd(button);
        }
    };

    const addButtonAtEnd = (button) => {
        let recipe = document.getElementById('editable-content');
        recipe.appendChild(button);
    };

    const findChildNode = (container, nodeText) => {
        let result;
        container.childNodes.forEach(child => {
            if (child.nodeValue === nodeText) {
                result = child;
            }
        });
        return result;
    };

    const addRecipeOutput = (values) => {
        let idx = recipeOutput.length;
        setRecipeOutput((prevOutput) => [...prevOutput, {
            id: null,
            idx: idx,
            amountGrams: values.amountGrams,
            title: values.title,
        }]);
    };

    const handleRecipeOutputRemove = (outputIdx) => {
        setRecipeOutput((prevOutput) => {
            const updatedOutput = prevOutput.filter(item => item.idx !== outputIdx);
            return updatedOutput.map((item, index) => ({
                ...item,
                idx: index,
            }));
        });
    };

    const getButton = (value) => {
        let uuid = generateUUID();
        let button = document.createElement('button');
        button.setAttribute('class', 'dynamic-recipe-button');
        button.setAttribute('contentEditable', 'false');
        button.setAttribute('id', 'dynamic-button-' + uuid);
        button.textContent = value + ' g';
        return button;
    };

    const setTitle = (event) => {
        event.preventDefault();
        recipeTitle = event.target.value;
        setButtonDisabled(!recipeTitle.length > 0);
        setFormValid(recipeTitle.length > 0);
    };

    const submit = () => {
        if (formValid || document.getElementById('recipe-title').value.length > 0) {
            let text = getRecipeText();
            let recipe = {
                title: recipeTitle,
                recipeText: text,
                dynamicQuantities: dynamicQuantities,
                recipeOutput: recipeOutput,
            };
            handleSubmit(recipe);
        } else {
            setButtonDisabled(true);
        }
    };

    const getRecipeText = () => {
        let text = '';
        let recipeContainer = document.getElementById('editable-content');
        recipeContainer.childNodes.forEach(child => {
            if (child.nodeName === '#text') {
                text = text + child.nodeValue;
            } else {
                let quantity = +child.innerText.replace(' g', '');
                dynamicQuantities.push({
                    quantity: quantity,
                    idx: text.length,
                });
            }
        });
        return text.replaceAll('\u00a0', ' ');
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const clipboardData = event.clipboardData || window.clipboardData;
        const pastedText = clipboardData.getData('text/plain');
        const selection = window.getSelection();
        if (selection.rangeCount) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const textNode = document.createTextNode(pastedText);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.setEndAfter(textNode);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }

    return (
        <>
            <div className={'productBlock'}>
                <SearchDropdownSelect style={{ width: '100%' }}
                                      id={selectedProduct.id}
                                      onSelect={(id) => setSelectedProduct(productsList.filter(it => it.id === id)[0])}
                                      data={productsList.map(it => ({ id: it.id, name: it.name }))}>
                </SearchDropdownSelect>
                <RecipeModal product={selectedProduct} onClick={handleAddProduct} />
            </div>
            <div className={'mb-3'}>
                <span>{t('title')}</span>
                <input type={'text'}
                       className={buttonDisabled ? 'form-control is-invalid' : 'form-control'}
                       id={'recipe-title'}
                       defaultValue={recipeTitle}
                       onChange={setTitle} />
                {buttonDisabled ? <span className={'warning-text'}>{t('validations:required')}</span> : <></>}
            </div>
            <DynamicQuantity onClick={addDynamicQuantity} selection={window.getSelection()} />
            <div>
                <span>{t('recipeText')}</span>
                <div contentEditable={true}
                     id={'editable-content'}
                     dangerouslySetInnerHTML={{ __html: recipeText }}
                     className={'form-control recipe-textarea'}
                     onPaste={handlePaste}>
                </div>
            </div>
            <RecipeOutputModal onClick={addRecipeOutput}></RecipeOutputModal>
            {recipeOutput != null && recipeOutput.length > 0
                ? <RecipeOutputTable items={recipeOutput}
                                     onRemove={handleRecipeOutputRemove}>
                </RecipeOutputTable>
                : <></>}
            <div className='center'>
                <SaveButton onClick={submit} disabled={buttonDisabled} />
                <CancelButton onClick={onClose} />
            </div>
        </>

    );
};